/**
 * FunnelStageChips
 *
 * Chip "Funil - Etapa" das listas de conversa dos 4 inboxes (wa-personal,
 * wa-business, instagram, messenger). Um componente só: a fiação de cada
 * shell passa as etiquetas do contato (ids), o catálogo e os funis.
 *
 *  - Regra de sempre (flag `multi_funnel` desligada): UM chip, o da primeira
 *    etiqueta conhecida, igual ao que cada shell desenhava inline.
 *  - Multi-funil (flag ligada): um chip POR FUNIL (uma etapa por funil), com
 *    o funil identificado em cada um. Acima de `max` chips, o excedente vira
 *    "+N" com o detalhe no title.
 *
 * Trunca a etapa por code points (não corta emoji no meio), como antes.
 */
import React from 'react';
import { getFeatureFlag } from '../../hooks/useFeatureFlags';

const STAGE_MAX_CHARS = 20;

function truncateStage(name) {
  if (!name) return null;
  const chars = Array.from(String(name));
  return chars.length > STAGE_MAX_CHARS
    ? `${chars.slice(0, STAGE_MAX_CHARS).join('').trimEnd()}…`
    : name;
}

/**
 * Resolve as etiquetas de um contato em entradas {label, funnelName}, uma por
 * funil quando a flag está ligada (a primeira etiqueta de cada funil vence),
 * ou só a primeira conhecida sem a flag. Exportado para o `hasBadgeRow` dos
 * shells e para quem precisa da mesma decisão sem renderizar.
 */
export function resolveStageEntries(labelIds, labelsById, funnels, multi = getFeatureFlag('multi_funnel')) {
  const known = (labelIds || []).map((id) => (labelsById || {})[id]).filter(Boolean);
  if (!known.length) return [];
  const funnelName = (label) => (funnels || []).find((f) => f.id === label.pipeline_id)?.name || null;
  if (!multi) {
    const label = known[0];
    return [{ label, funnelName: funnelName(label) }];
  }
  const seen = new Set();
  const out = [];
  for (const label of known) {
    const fid = label.pipeline_id || '';
    if (seen.has(fid)) continue;
    seen.add(fid);
    out.push({ label, funnelName: funnelName(label) });
  }
  return out;
}

const chipStyle = (colors) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  backgroundColor: colors.bgSecondary, color: colors.textSecondary,
  fontSize: 10, fontWeight: 600, padding: '1px 6px',
  borderRadius: 4, flexShrink: 1, minWidth: 0, maxWidth: 190,
  lineHeight: '16px',
});

const FunnelStageChips = ({ labelIds, labelsById, funnels, colors, max = 2 }) => {
  const entries = resolveStageEntries(labelIds, labelsById, funnels);
  const visible = entries.filter((e) => e.funnelName);
  if (!visible.length) return null;
  const shown = visible.slice(0, max);
  const extra = visible.slice(max);
  return (
    <>
      {shown.map(({ label, funnelName }) => {
        const stageShort = truncateStage(label.name);
        const text = stageShort ? `${funnelName} - ${stageShort}` : funnelName;
        return (
          <span
            key={label.id}
            style={chipStyle(colors)}
            title={`Funil: ${funnelName}${label.name ? ' · ' + label.name : ''}`}
          >
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              backgroundColor: label.color, flexShrink: 0,
            }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {text}
            </span>
          </span>
        );
      })}
      {extra.length > 0 && (
        <span
          style={{ ...chipStyle(colors), flexShrink: 0 }}
          title={extra.map(({ label, funnelName }) => `${funnelName} · ${label.name}`).join('\n')}
        >
          +{extra.length}
        </span>
      )}
    </>
  );
};

export default FunnelStageChips;
